import { Role } from "@cryptors/api-client";
import { AccessControlProvider } from "@refinedev/core";

import { getCurrentRole } from "./authProvider";

// Resource/action gating per role. Backend authorization remains the source of
// truth; this layer also guards direct page navigation and hides unavailable UI.
//
// - ADMIN     : everything
// - REDACTEUR : content editor — read + create/edit, except ADMIN-only areas
// - ANALYSTE  : read-only content + dashboard, except ADMIN-only areas
// - CLIENT    : scoped dashboard only
const READONLY_ACTIONS = new Set(["list", "show"]);
const CONTENT_RESOURCES = new Set([
  "advertisements",
  "announcements",
  "avatars",
  "cities",
  "cryptors",
  "faq",
  "hints",
  "levels",
  "questions",
  "translation",
]);
const ADMIN_ONLY_RESOURCES = new Set([
  "users",
  "ai-training",
  "chat-message",
  "quiz",
]);

const accessControlProvider: AccessControlProvider = {
  can: async ({ action, resource }) => {
    const role = getCurrentRole();

    if (role === Role.ADMIN) return { can: true };

    if (role === Role.CLIENT) {
      const can = resource === "dashboard" && action === "list";
      return {
        can,
        reason: can ? undefined : "Accès limité au tableau de bord",
      };
    }

    if (ADMIN_ONLY_RESOURCES.has(resource ?? "")) {
      return { can: false, reason: "Accès réservé aux administrateurs" };
    }

    if (role === Role.REDACTEUR) {
      if (!CONTENT_RESOURCES.has(resource ?? "")) {
        return { can: false, reason: "Ressource non autorisée" };
      }
      // Default delete buttons are hidden; DeleteGuardButton surfaces an
      // explicit explanation where an inline delete affordance is useful.
      if (action === "delete") {
        return {
          can: false,
          reason: "Suppression réservée aux administrateurs",
        };
      }
      return { can: true };
    }

    if (role === Role.ANALYSTE) {
      const can =
        (resource === "dashboard" && action === "list") ||
        (CONTENT_RESOURCES.has(resource ?? "") && READONLY_ACTIONS.has(action));
      return {
        can,
        reason: can ? undefined : "Accès en lecture seule",
      };
    }

    return { can: false };
  },
  options: {
    buttons: {
      enableAccessControl: true,
      hideIfUnauthorized: true,
    },
  },
};

export default accessControlProvider;
